import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import routes from './routes/Routes';
import fetchUserCountry from './api/getUserCountry';
import { useDeviceToken } from './hooks/useDeviceToken';
import { getFirebaseConfig, forceFirebaseEnvironment, setupTokenRefreshListener, diagnoseFirebaseTokenIssue } from './config/firebase';
import { UnreadCountProvider } from './contexts/UnreadCountContext';
import { setupApiInterceptor } from './utils/apiInterceptor';
import monitoringService from './services/monitoringService';
import { loadGoogleAnalytics, trackPageView } from './utils/googleAnalytics';

fetchUserCountry();

// Initialize Google Analytics on app start
loadGoogleAnalytics();

// Router observer component to track page views
function RouterObserver() {
  const location = useLocation();

  useEffect(() => {
    const pageName = location.pathname;

    // Track page view in monitoring service
    monitoringService.trackPageView(pageName, {
      search: location.search,
      hash: location.hash,
    }).catch(() => {});

    // Track page view in Google Analytics
    trackPageView(pageName + location.search, {
      page_title: document.title,
    });
  }, [location]);

  return null;
}

function App() {
  const { registerDevice, updateFirebaseToken, resetDeviceRegistration, testDeviceRegistration } = useDeviceToken();

  // Initialize monitoring service
  useEffect(() => {
    // Setup API interceptor
    setupApiInterceptor();

    // Track app open
    monitoringService.trackAppOpen().catch(() => {});

    // Try to fetch user info for monitoring
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      monitoringService.fetchUserPhoneNumber().catch(() => {});
    }

    // Track app close
    const handleBeforeUnload = () => {
      monitoringService.trackAppClose().catch(() => {});
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      monitoringService.destroy();
    };
  }, []);

  // Register device when app starts and try to get FCM token
  useEffect(() => {
    // Check if user is logged in
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return;
    }

    const isDeviceRegistered = localStorage.getItem('device_registered') === 'true';
    const isDeviceRegistrationDisabled = localStorage.getItem('device_registration_disabled') === 'true';
    const lastRegistrationAttempt = localStorage.getItem('device_registration_last_attempt');
    const now = Date.now();
    
    // Check if registration was attempted recently (within last 5 minutes)
    const recentlyAttempted = lastRegistrationAttempt && (now - parseInt(lastRegistrationAttempt)) < 300000;
    
    // Try to get FCM token and register/update device
    const tryRegisterWithToken = async () => {
      try {
        const { getFirebaseToken } = await import('./utils/firebaseUtils');
        const token = await getFirebaseToken();
        
        if (token) {
          if (isDeviceRegistered) {
            // Update token for existing device
            await updateFirebaseToken(token);
          } else if (!isDeviceRegistrationDisabled && !recentlyAttempted) {
            // Register new device with token
            await registerDevice(token);
          }
        } else if (!isDeviceRegistered && !isDeviceRegistrationDisabled && !recentlyAttempted) {
          // Register without token if token unavailable
          registerDevice();
        }
      } catch (error) {
        // Failed to get FCM token
        // Still try to register without token
        if (!isDeviceRegistered && !isDeviceRegistrationDisabled && !recentlyAttempted) {
          registerDevice();
        }
      }
    };

    tryRegisterWithToken();
  }, [registerDevice, updateFirebaseToken]);

  // Listen for login events to register device
  useEffect(() => {
    const handleLogin = () => {
      // Check if device is already registered
      const isDeviceRegistered = localStorage.getItem('device_registered') === 'true';
      const isDeviceRegistrationDisabled = localStorage.getItem('device_registration_disabled') === 'true';
      const lastRegistrationAttempt = localStorage.getItem('device_registration_last_attempt');
      const now = Date.now();
      const recentlyAttempted = lastRegistrationAttempt && (now - parseInt(lastRegistrationAttempt)) < 300000;
      
      if (!isDeviceRegistered && !isDeviceRegistrationDisabled && !recentlyAttempted) {
        registerDevice();
      }
    };

    // Listen for custom login event
    window.addEventListener('userLogin', handleLogin);
    
    return () => {
      window.removeEventListener('userLogin', handleLogin);
    };
  }, [registerDevice]);

  // Setup FCM token refresh listener
  useEffect(() => {
    // Only set up if user is logged in
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return;
    }

    // Set up token refresh listener asynchronously
    // When Firebase refreshes the token, update it in backend
    let isMounted = true;
    let refreshListener = null;
    
    setupTokenRefreshListener((newToken) => {
      if (newToken && isMounted) {
        updateFirebaseToken(newToken);
      }
    }).then((result) => {
      refreshListener = result;
    }).catch((error) => {
      // Failed to set up token refresh listener
    });

    return () => {
      isMounted = false;
      // Cleanup interval if listener was set up
      if (refreshListener && refreshListener.cleanup) {
        refreshListener.cleanup();
      }
    };
  }, [updateFirebaseToken]);

  // Expose functions globally for testing
  useEffect(() => {
    window.updateFirebaseToken = updateFirebaseToken;
    window.resetDeviceRegistration = resetDeviceRegistration;
    window.testDeviceRegistration = testDeviceRegistration;
    
    // Add functions to disable/enable device registration
    window.disableDeviceRegistration = () => {
      localStorage.setItem('device_registration_disabled', 'true');
    };
    
    window.enableDeviceRegistration = () => {
      localStorage.removeItem('device_registration_disabled');
    };
    
    // Add function to clear all device registration flags
    window.clearDeviceRegistrationFlags = () => {
      localStorage.removeItem('device_registered');
      localStorage.removeItem('device_registration_disabled');
      localStorage.removeItem('device_registration_last_attempt');
      localStorage.removeItem('firebase_token_last_attempt');
    };
    
    // Add Firebase environment testing functions
    window.getFirebaseConfig = getFirebaseConfig;
    window.forceFirebaseEnvironment = forceFirebaseEnvironment;
    window.testFirebaseConfig = () => {
      return {
        current: getFirebaseConfig(),
        dev: getFirebaseConfig('dev'),
        prod: getFirebaseConfig('prod')
      };
    };
    
    // Test Firebase token generation
    window.testFirebaseToken = async () => {
      try {
        const { getFirebaseToken } = await import('./utils/firebaseUtils');
        const token = await getFirebaseToken();
        return token;
      } catch (error) {
        return null;
      }
    };
    
    // Check Firebase SDK loading
    window.checkFirebaseSDK = () => {
      return {
        sdkLoaded: !!window.firebase,
        appsCount: window.firebase?.apps?.length || 0,
        messagingAvailable: !!window.firebase?.messaging
      };
    };
    
    // Diagnose why Firebase token might be empty
    window.diagnoseFirebaseToken = async () => {
      const diagnostics = await diagnoseFirebaseTokenIssue();
      return diagnostics;
    };
    
    // Clear Firebase error flags and reset token request
    window.resetFirebaseTokenState = () => {
      localStorage.removeItem('firebase_unavailable');
      localStorage.removeItem('firebase_token_last_attempt');
    };
    
    // Force get Firebase token (clears flags and attempts to get token)
    window.forceGetFirebaseToken = async () => {
      // Clear error flags first
      localStorage.removeItem('firebase_unavailable');
      localStorage.removeItem('firebase_token_last_attempt');
      
      // Try to get token
      const { getFirebaseToken } = await import('./utils/firebaseUtils');
      const token = await getFirebaseToken();
      
      if (token) {
        // Check if device is already registered
        const isDeviceRegistered = localStorage.getItem('device_registered') === 'true';
        
        if (isDeviceRegistered) {
          // Update token for existing device
          await updateFirebaseToken(token);
        } else {
          // Register device with the new token
          await registerDevice(token);
        }
        
        return token;
      } else {
        return null;
      }
    };
    
    return () => {
      delete window.updateFirebaseToken;
      delete window.resetDeviceRegistration;
      delete window.testDeviceRegistration;
      delete window.disableDeviceRegistration;
      delete window.enableDeviceRegistration;
      delete window.getFirebaseConfig;
      delete window.forceFirebaseEnvironment;
      delete window.testFirebaseConfig;
      delete window.testFirebaseToken;
      delete window.checkFirebaseSDK;
      delete window.diagnoseFirebaseToken;
      delete window.resetFirebaseTokenState;
      delete window.forceGetFirebaseToken;
    };
  }, [updateFirebaseToken, resetDeviceRegistration, testDeviceRegistration, registerDevice]);

  return (
    <div className="App">
      <UnreadCountProvider>
        <Router>
          <RouterObserver />
          <Routes>
            {routes.map(({ path, element }, index) => (
              <Route key={index} path={path} element={element} />
            ))}
          </Routes>
        </Router>
      </UnreadCountProvider>
    </div>
  );
}

export default App;